/**
 * Tiny framework-free DOM helpers. No virtual DOM, no dependencies — just
 * ergonomic `document.createElement` wrappers. Together with the Web Components
 * in @mb-group/ui-components this is the whole "rendering engine" (ADR 0003).
 */

export type Child = Node | string | number | null | undefined | false;

type AttrValue = string | number | boolean | null | undefined | EventListenerOrEventListenerObject;

/** Attribute keys that map to a DOM property rather than an HTML attribute. */
const PROP_KEYS = new Set(["value", "checked", "disabled", "selected"]);

const asStr = (v: unknown): string =>
	typeof v === "string"
		? v
		: typeof v === "number" || typeof v === "boolean"
			? String(v)
			: JSON.stringify(v);

/**
 * Create an element. String/number children become text nodes; falsy children
 * are skipped. Keys starting with `on` bind an event listener; everything else
 * becomes an attribute (booleans toggle presence).
 *
 * A string tag (e.g. a custom element like "mb-badge") is also accepted and
 * returns a generic HTMLElement.
 */
export type Attrs = Record<string, AttrValue> | null | undefined;

export function el<K extends keyof HTMLElementTagNameMap>(
	tag: K,
	attrs?: Attrs,
	...children: Child[]
): HTMLElementTagNameMap[K];
export function el(tag: string, attrs?: Attrs, ...children: Child[]): HTMLElement;
export function el(tag: string, attrs?: Attrs, ...children: Child[]): HTMLElement {
	const node = document.createElement(tag);
	if (attrs) {
		for (const [key, value] of Object.entries(attrs)) {
			if (value === null || value === undefined || value === false) continue;
			if (key.startsWith("on") && typeof value === "function") {
				node.addEventListener(key.slice(2).toLowerCase(), value);
				continue;
			}
			if (key === "class") {
				node.className = asStr(value);
				continue;
			}
			if (PROP_KEYS.has(key)) {
				// Reflect to the property so form controls behave correctly.
				(node as unknown as Record<string, unknown>)[key] = value;
				if (value === true) node.setAttribute(key, "");
				continue;
			}
			node.setAttribute(key, value === true ? "" : asStr(value));
		}
	}
	append(node, children);
	return node;
}

/** Append a flat list of children to a parent, skipping falsy values. */
export function append(parent: Node, children: Child[]): void {
	for (const child of children) {
		if (child === null || child === undefined || child === false) continue;
		parent.appendChild(
			typeof child === "string" || typeof child === "number"
				? document.createTextNode(String(child))
				: child,
		);
	}
}

/** Remove all children of a node. */
export function clear(node: Node): void {
	while (node.firstChild) node.removeChild(node.firstChild);
}

/**
 * Parse a trusted HTML string (CMS-authored rich text delivered via GraphQL)
 * into a document fragment. Only ever call this with server-owned content.
 */
export function fromTrustedHtml(html: string): DocumentFragment {
	const tpl = document.createElement("template");
	tpl.innerHTML = html;
	return tpl.content;
}

/** Convenience: a text node. */
export function text(value: string | number): Text {
	return document.createTextNode(asStr(value));
}

/** Query a single element, typed. */
export function qs<T extends Element = HTMLElement>(root: ParentNode, selector: string): T | null {
	return root.querySelector<T>(selector);
}

/**
 * Build an inline SVG node from a trusted markup string. Used for the authored
 * illustrations (see components/circular.ts) — never for remote input.
 */
export function svgFromMarkup(markup: string): SVGElement | null {
	const tpl = document.createElement("template");
	tpl.innerHTML = markup.trim();
	const node = tpl.content.firstElementChild;
	return node instanceof SVGElement ? node : null;
}
